import DOMPurify from 'dompurify';
import { supabase } from '../lib/supabase';

/**
 * Security Service
 * Handles input sanitization, CSRF protection, and file validation
 */

/**
 * Sanitize user input to prevent XSS attacks
 */
export function sanitizeInput(input) {
  if (typeof input !== 'string') return input;
  
  return DOMPurify?.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  });
}

/**
 * Sanitize HTML content (for rich text editors)
 */
export function sanitizeHTML(html) {
  if (typeof html !== 'string') return html;
  
  return DOMPurify?.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false
  });
}

/**
 * Sanitize object recursively
 */
export function sanitizeObject(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return sanitizeInput(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj?.map(item => sanitizeObject(item));
  }
  
  const sanitized = {};
  Object.keys(obj)?.forEach(key => {
    sanitized[key] = sanitizeObject(obj?.[key]);
  });
  
  return sanitized;
}

/**
 * Validate file upload
 */
export async function validateFileUpload(file) {
  try {
    // Get allowed settings from database
    const { data: settings } = await supabase
      ?.from('system_settings')
      ?.select('setting_value')
      ?.in('setting_key', ['file_upload_max_size_mb', 'file_upload_allowed_types'])
      ?.then(result => {
        const settingsMap = {};
        result?.data?.forEach(setting => {
          if (setting?.setting_key === 'file_upload_max_size_mb') {
            settingsMap.maxSizeMB = parseInt(setting?.setting_value);
          } else if (setting?.setting_key === 'file_upload_allowed_types') {
            settingsMap.allowedTypes = setting?.setting_value?.split(',');
          }
        });
        return settingsMap;
      });
    
    const maxSizeMB = settings?.maxSizeMB || 50;
    const allowedTypes = settings?.allowedTypes || [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf'
    ];
    
    // Check file size
    const fileSizeMB = file?.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      return {
        valid: false,
        error: `File size exceeds maximum allowed size of ${maxSizeMB}MB`
      };
    }
    
    // Check MIME type
    if (!allowedTypes?.includes(file?.type)) {
      return {
        valid: false,
        error: `File type ${file?.type} is not allowed. Allowed types: ${allowedTypes?.join(', ')}`
      };
    }
    
    // Check file extension matches MIME type
    const extension = file?.name?.split('.')?.pop()?.toLowerCase();
    const mimeExtensionMap = {
      'image/jpeg': ['jpg', 'jpeg'],
      'image/png': ['png'],
      'image/gif': ['gif'],
      'image/webp': ['webp'],
      'application/pdf': ['pdf']
    };
    
    const expectedExtensions = mimeExtensionMap?.[file?.type] || [];
    if (!expectedExtensions?.includes(extension)) {
      return {
        valid: false,
        error: 'File extension does not match file type'
      };
    }
    
    return { valid: true };
  } catch (error) {
    console.error('Validate file upload error:', error);
    return {
      valid: false,
      error: 'File validation failed'
    };
  }
}

/**
 * Check rate limit.
 *
 * @param {string} endpointType  - 'auth' | 'file_upload' | 'api'
 * @param {string} [identifier] - Optional pre-auth identifier (e.g. email being
 *                                attempted). Used when there is no authenticated
 *                                user yet so the DB still has something to count
 *                                against. Never stored in plain text — caller
 *                                should pass a value they are comfortable
 *                                persisting in rate_limit_events.ip_address.
 */
export async function checkRateLimit(endpointType, identifier = null) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();

    // For pre-auth calls (e.g. login attempts) there is no userId yet.
    // Pass the provided identifier (typically hashed email) as p_ip_address so
    // the DB function has at least one non-null column to GROUP BY.
    const { data, error } = await supabase?.rpc('check_rate_limit', {
      p_user_id: user?.id || null,
      p_ip_address: user?.id ? null : (identifier || null),
      p_endpoint_type: endpointType
    });

    if (error) throw error;

    return { allowed: data, success: true };
  } catch (error) {
    console.error('Check rate limit error:', error);
    // Fail CLOSED — a broken rate-limit check should not grant access.
    // Legitimate users will retry; attackers must not get a free pass.
    return { allowed: false, success: false };
  }
}

/**
 * CSRF Protection — Double-Submit Cookie Pattern (H-04)
 *
 * Primary CSRF defence: this app uses JWT Bearer tokens in Authorization
 * headers for all Supabase calls. Cross-origin requests cannot include
 * custom headers (blocked by CORS), so Bearer-token auth is already
 * CSRF-resistant at the protocol level.
 *
 * These helpers provide defence-in-depth for any non-Bearer flows:
 *   1. A random token is stored in a SameSite=Strict, JS-readable cookie.
 *   2. It is also sent as an X-CSRF-Token request header.
 *   3. The server verifies that the header value matches the cookie value.
 *      A cross-site attacker can trigger a request with the cookie attached
 *      automatically, but cannot read the cookie from another origin to
 *      know what to put in the header — that mismatch is what the server
 *      detects.
 *
 * Comparison is always constant-time to prevent timing side-channels.
 */

const CSRF_COOKIE = 'csrf_token';
export const CSRF_HEADER_NAME = 'X-CSRF-Token';

function setCsrfCookie(token) {
  // SameSite=Strict: cookie never sent on cross-site navigations.
  // NOT httpOnly — JS needs to read it for the double-submit pattern.
  // Secure  — only sent over HTTPS in production.
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CSRF_COOKIE}=${token}; SameSite=Strict; Path=/${secure}`;
}

function readCsrfCookie() {
  return (
    document.cookie
      .split('; ')
      .find(row => row.startsWith(`${CSRF_COOKIE}=`))
      ?.split('=')[1] ?? null
  );
}

/** Constant-time string comparison — prevents CSRF-token timing side-channels. */
function timingSafeEqualStr(a, b) {
  const enc = new TextEncoder();
  const bufA = enc.encode(typeof a === 'string' ? a : '');
  const bufB = enc.encode(typeof b === 'string' ? b : '');
  let diff = bufA.length ^ bufB.length;
  const len = Math.max(bufA.length, bufB.length);
  for (let i = 0; i < len; i++) {
    diff |= (bufA[i] ?? 0) ^ (bufB[i] ?? 0);
  }
  return diff === 0;
}

/**
 * Generate a new CSRF token and persist it in the SameSite cookie.
 * Call once on session start / page load.
 */
export function generateCSRFToken() {
  const token = crypto.randomUUID();
  setCsrfCookie(token);
  return token;
}

/**
 * Validate an incoming CSRF token against the cookie value.
 * Uses constant-time comparison to prevent timing attacks.
 * Returns false when no cookie exists (uninitialized session).
 */
export function validateCSRFToken(token) {
  const stored = readCsrfCookie();
  if (!stored) return false;
  return timingSafeEqualStr(stored, token);
}

/**
 * Return the current CSRF token, generating one if absent.
 * Attach this value as an X-CSRF-Token header on all non-GET requests.
 */
export function getCSRFToken() {
  return readCsrfCookie() ?? generateCSRFToken();
}